import { useEffect } from "react";

/**
 * Injects a <script type="application/ld+json"> element into document.head
 * for the lifetime of the component. Removes it on unmount / data change.
 *
 * @param {object|null} data  — JSON-serializable Schema.org object.
 * @param {string} keyId      — Unique dom id for this script (allows multiple LDs).
 */
export default function JsonLd({ data, keyId = "acrepulse-ld" }) {
  useEffect(() => {
    if (!data) return;
    let el = document.getElementById(keyId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = keyId;
      document.head.appendChild(el);
    }
    try {
      el.text = JSON.stringify(data);
    } catch {
      el.text = "{}";
    }
    return () => {
      const n = document.getElementById(keyId);
      if (n && n.parentNode) n.parentNode.removeChild(n);
    };
  }, [data, keyId]);
  return null;
}

/* -------------------- Builders (defensive; skip null fields) -------------------- */

const SQFT_PER_ACRE = 43560;

// Maps AcrePulse conversion_status → Schema.org mainEntity @type
export function parcelMainEntityType(conversionStatus = "") {
  const s = String(conversionStatus).toLowerCase();
  if (s.includes("residential")) return "SingleFamilyResidence";
  if (s.includes("industrial") || s.includes("commercial")) return "CommercialProperty";
  return "Land"; // Agricultural, Converted NA, and default
}

// Maps AcrePulse project property_type → Schema.org mainEntity @type
export function projectMainEntityType(propertyType = "") {
  const s = String(propertyType).toLowerCase();
  if (s.includes("apartment") || s.includes("villa") || s.includes("residential")) return "SingleFamilyResidence";
  if (s.includes("commercial") || s.includes("office")) return "CommercialProperty";
  return "Land"; // plots, parcels
}

function cleanObject(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === "") continue;
    if (typeof v === "object" && !Array.isArray(v)) {
      const nested = cleanObject(v);
      if (Object.keys(nested).length > 0) out[k] = nested;
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Build a RealEstateListing JSON-LD payload for a parcel.
 * Any missing/optional field is safely omitted.
 */
export function buildParcelJsonLd(parcel, opts = {}) {
  if (!parcel) return null;
  const url = opts.url || (typeof window !== "undefined" ? window.location.href : undefined);
  const mainType = parcelMainEntityType(parcel.conversion_status);
  const sqft = typeof parcel.size_acres === "number" ? Math.round(parcel.size_acres * SQFT_PER_ACRE) : null;
  const totalPrice =
    typeof parcel.price_per_acre === "number" && typeof parcel.size_acres === "number"
      ? Math.round(parcel.price_per_acre * parcel.size_acres)
      : null;

  const geo =
    typeof parcel.lat === "number" && typeof parcel.lng === "number"
      ? { "@type": "GeoCoordinates", latitude: parcel.lat, longitude: parcel.lng }
      : null;

  const address = {
    "@type": "PostalAddress",
    streetAddress: parcel.title || undefined,
    addressLocality: parcel.corridor_location || undefined,
    addressRegion: "Karnataka",
    postalCode: opts.postalCode || undefined,
    addressCountry: "IN",
  };

  const description = [
    parcel.title,
    parcel.conversion_status && `Land type: ${parcel.conversion_status}`,
    parcel.khata_type && `Khata: ${parcel.khata_type}`,
    typeof parcel.legal_score === "number" && `Legal score: ${parcel.legal_score}/10`,
    typeof parcel.neighbourhood_score === "number" && `Neighbourhood score: ${parcel.neighbourhood_score}/100`,
    typeof parcel.road_width_ft === "number" && `Road width: ${parcel.road_width_ft} ft`,
  ]
    .filter(Boolean)
    .join(" · ");

  const payload = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: parcel.title,
    url,
    description,
    datePosted: parcel.created_at,
    mainEntity: cleanObject({
      "@type": mainType,
      address,
      geo,
      floorSize: sqft
        ? { "@type": "QuantitativeValue", value: sqft, unitCode: "FTK" } // FTK = square foot (UN/ECE)
        : null,
    }),
    offers: totalPrice
      ? {
          "@type": "Offer",
          price: String(totalPrice),
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          offeredBy: { "@type": "Organization", name: "AcrePulse" },
        }
      : undefined,
  };
  return cleanObject(payload);
}

/**
 * Build a RealEstateListing JSON-LD payload for a Project (Tier 1/2/3 listing).
 */
export function buildProjectJsonLd(project, opts = {}) {
  if (!project) return null;
  const url = opts.url || (typeof window !== "undefined" ? window.location.href : undefined);
  const mainType = projectMainEntityType(project.property_type);

  const address = {
    "@type": "PostalAddress",
    streetAddress: project.location || undefined,
    addressLocality: project.location || undefined,
    addressRegion: "Karnataka",
    addressCountry: "IN",
  };

  const payload = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: project.project_name,
    url,
    description: [
      project.builder_name && `By ${project.builder_name} (${project.tier})`,
      project.property_type,
      project.description,
    ]
      .filter(Boolean)
      .join(" · "),
    datePosted: project.created_at,
    image: project.image_url || undefined,
    mainEntity: cleanObject({
      "@type": mainType,
      address,
    }),
    offers:
      typeof project.price_min === "number"
        ? {
            "@type": "AggregateOffer",
            lowPrice: String(Math.round(project.price_min)),
            highPrice: String(Math.round(project.price_max || project.price_min)),
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            offeredBy: { "@type": "Organization", name: "AcrePulse" },
          }
        : undefined,
  };
  return cleanObject(payload);
}
